import React, { useState, useEffect } from "react";
import { useAddItemToCart, useCart } from "src/context/siteContext";
import { SanityImage } from "../../interfaces/sanity";
import { encode } from "shopify-gid";
import Ribbon from "src/components/Ribbon";
import { eventData } from 'src/utils/dataLayer';
import {
  ContainerLink,
  TextContainer,
  Heading,
  InfoContainer,
  DescriptionHeading,
  Description,
  PriceHeading,
  StyledRating,
  CartLink,
  StyledImage,
  ImageContainer,
  NewProductMessage,
  RatingsBox,
  RatingParagraph,
  Label
} from "./styles";
import { useTheme } from "styled-components";
import trackKlavyioAddedToCart from "src/utils/mapKlaviyoPayload";
import locales from 'src/locales';

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

const PRODUCT_VARIANT_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      variants(first: 100) {
        edges {
          node {
            id
            title
            availableForSale
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
          }
        }
      }
    }
  }
`;

interface Props {
  id: number;
  slug: string;
  title: string;
  okendoReviews: {
    totalReviews: number;
    rating: number;
  }
  image: SanityImage;
  price: string;
  className?: string;
  description?: string;
  ribbon?: string;
  mobileSmall?: boolean;
  addToCart?: boolean;
  mobilePhoto?: SanityImage;
  ribbonSmall?: boolean;
  onClick?: () => void;
  large?: boolean;
  showPrice?: boolean;
  showRating?: boolean;
  buttonBackgroundColors?: object;
  buttonColors?: object;
  productQuantity?: object;
  label: string;
  labelTxtColor: object;
  labelBgColor: object;
  buttonsInBlack?: boolean;
  newProductMessage?: boolean;
}

const ProductItem = ({
  id,
  slug,
  title,
  okendoReviews,
  image,
  price,
  className,
  description,
  ribbon,
  mobileSmall,
  addToCart = false,
  mobilePhoto,
  ribbonSmall = false,
  large,
  showPrice,
  showRating,
  buttonColors,
  buttonBackgroundColors,
  newProductMessage,
  productQuantity,
  label,
  labelTxtColor,
  labelBgColor,
  buttonsInBlack,
  onClick,
}: Props) => {
  const theme = useTheme();
  const { checkout } = useCart();
  const { addItemToCart } = useAddItemToCart();
  const [available, setAvailable] = useState(false);
  const [variants, setVariants] = useState([]);
  const [check, setCheck] = useState(true);
  const [activeVariantId, setActiveVariantId] = useState("" as string);
  const [compareAtPrice, setCompareAtPrice] = useState(
    undefined as string | undefined
  );
  const [adding, setAdding] = useState<boolean>(false);
  const hasComparePrice =
    compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);
  const currencySymbol = process.env.GATSBY_CURRENCY_SYMBOL;
  const hasProductQuantityPricing =
    productQuantity?.originPrice &&
    productQuantity?.salesPrice &&
    parseFloat(productQuantity.originPrice) !==
      parseFloat(productQuantity.salesPrice);

  const getVariantCompareAt = (variant: any) => {
    if (!variant) return undefined;
    if (variant.compareAtPrice) return variant.compareAtPrice;
    return undefined;
  };

  useEffect(() => {
    if (check) {
      const shopifyId = encode("Product", id, {
        accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
      });

      const fetchProduct = async () => {
        try {
          const { data } = await shopifyFetch({
            query: PRODUCT_VARIANT_QUERY,
            variables: { id: shopifyId }
          });

          const product = data?.product;
          
          if (!product) return;

          const decodedVariants = product.variants.edges.map(({ node }: any) => {
            return {
              ...node,
              available: node.availableForSale, 
              price: node.price.amount,
              compareAtPrice: node.compareAtPrice?.amount,
              image: node.image ? { src: node.image.url, ...node.image } : null,
            };
          });

          setVariants(decodedVariants);
          
          if(decodedVariants.length > 0) {
            setActiveVariantId(decodedVariants[0].id as string);
            setAvailable(decodedVariants[0].available);
            
            const variantCompareAt = getVariantCompareAt(decodedVariants[0]);
            if (variantCompareAt) setCompareAtPrice(variantCompareAt);
          }

          setCheck(false);
        } catch (error) {
          console.error("Error fetching product", error);
        }
      };

      fetchProduct();
    }
  }, [check, id]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (available) {
      setAdding(true);
      // GA4 add to cart event
      const item = {
        name: title,
        id: variants[0]?.sku,
        image: variants[0]?.image?.src,
        product_id: id,
        price: parseFloat(price),
        brand: 'GroovePillows',
        variant: variants[0]?.title,
        quantity: 1,
      };
      dataLayer?.push({
      ...eventData('add_to_cart'),
        ecommerce: {
          value: item.price * item.quantity,
          currencyCode: process.env.GATSBY_CURRENCY_SYMBOL == '$' ? 'USD' : 'GBP',
          items: [item],
        }
      });
      trackKlavyioAddedToCart(checkout, item);
      // TriplePixel('AddToCart', { item: variants[0]?.sku, q: 1 });
      const attributes = [];

      // if (mobilePhoto) {
      //   attributes.push({ key: "mobilePhoto", value: mobilePhoto.asset._ref });
      // }

      addItemToCart(activeVariantId, 1, attributes).then(() => {
        setAdding(false);
      });
    }
  };

  let message = locales.productInfo.new_product_message;
  if (id == 6852326654038 || id == 6852243685462) {
      message = locales.productInfo.moreStock;
  }

  const renderPriceBlock = (isLarge: boolean) => {
    const PriceTag = isLarge ? PriceHeading : Heading;

    if (!showPrice) return null;

    if (hasProductQuantityPricing) {
      return (
        <div style={{display: "flex"}}>
          <PriceTag className="strikethrough_price">
            {currencySymbol + parseFloat(productQuantity.originPrice).toFixed(2)}
          </PriceTag>
          <PriceTag>
            {currencySymbol + parseFloat(productQuantity.salesPrice).toFixed(2)}
          </PriceTag>
        </div>
      );
    }

    if (hasComparePrice) {
      return (
        <div style={{display: "flex"}}>
          <PriceTag className="strikethrough_price">
            {currencySymbol + parseFloat(compareAtPrice as string).toFixed(2)}
          </PriceTag>
          <PriceTag>
            {currencySymbol + parseFloat(price).toFixed(2)}
          </PriceTag>
        </div>
      );
    }

    return <PriceTag>{currencySymbol + price}</PriceTag>;
  };

  return (
    <>

<ContainerLink
      className={`${className} product-container`}
      to={`/products/${slug}`}
      mobileSmall={mobileSmall}
      onClick={onClick}
    >
      <ImageContainer>
        {image && <StyledImage imageRef={mobilePhoto ? mobilePhoto : image} />}
      </ImageContainer>
      {ribbon && <Ribbon small={ribbonSmall}>{ribbon}</Ribbon>}
      {label && <Label background={labelBgColor} color={labelTxtColor}>{label}</Label>}
      <InfoContainer bigPadding={description ? true : false}>
        <TextContainer addToCart={addToCart}>
          {large ? (
            <>
              <DescriptionHeading>{title}</DescriptionHeading>
              {renderPriceBlock(true)}
            </>
          ) : (
            <>
              <Heading>{title}</Heading>
              {renderPriceBlock(false)}
            </>
          )}
          {description && <Description blocks={description} />}
        </TextContainer>
        {
        newProductMessage
        ? <NewProductMessage>{message}</NewProductMessage>
        : showRating && 
        <RatingsBox id="rating-box">
          <StyledRating fieldName="productItem" number={okendoReviews?.rating} color={theme.colors.yellow} />
          <RatingParagraph>
            <span>{okendoReviews?.totalReviews} Reviews</span>
          </RatingParagraph>
        </RatingsBox>
        }
        {addToCart && !buttonsInBlack && (
          <CartLink className="add-to-cart-cta" onClick={handleClick} btnBackground={buttonBackgroundColors?.value} disabled={!available} style={{backgroundColor: buttonBackgroundColors?.value, color: buttonColors?.value}}>
            {adding ? "Adding..." : "Add to cart"}
          </CartLink>
        )}
        {buttonsInBlack && (
          <CartLink className="add-to-cart-cta" to={`/products/${slug}`} buttonsInBlack={buttonsInBlack} btnBackground={buttonBackgroundColors?.value} style={{backgroundColor: buttonBackgroundColors?.value, color: buttonColors?.value}}>
            SHOP NOW
          </CartLink>
        )}
      </InfoContainer>
    </ContainerLink>

    </>

  );
};

export default ProductItem;
